import { useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import * as React from 'react'
import { useContext } from 'react'

export function createWithContextAndReduxStateHOC<ContextState>(
  context: React.Context<ContextState>,
  displayName: `with${string}`,
) {
  return function <R extends object, P>(
    mapContextAndAppStateToProps: (state: ContextState, appState: App.State, props: P) => R,
    WrappedComponent: React.ComponentType<P>,
    memoisationFunc?: (prevProps: Readonly<P>, nextProps: Readonly<P>) => boolean,
  ): React.FunctionComponent<Omit<P, keyof R>> {
    const componentName = WrappedComponent.displayName || WrappedComponent.name

    const Component = React.memo(function(props: any) {
      return <WrappedComponent {...props} />
    }, memoisationFunc)

    function HOCWrapper(props: P) {
      const contextState = useContext(context)
      const propsFromState = useAppSelector(state => mapContextAndAppStateToProps(contextState, state, props), shallowEqual)

      return <Component {...props} {...propsFromState} />
    }

    HOCWrapper.displayName = `${displayName}(${componentName})`
    return HOCWrapper
  }
}
